import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import RequestService from '../services/requestService';
import ChatInterface from '../components/chat/ChatInterface';
import ProcessStepper from '../components/common/ProcessStepper';
import AvailabilitySummary from '../components/availability/AvailabilitySummary';
import DateUtils from '../utils/dateUtils';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  ChevronLeft as ChevronLeftIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import MainLayout from '../layout/MainLayout';


const NegotiationPage = () => {
  // Refs für die Höhenanpassung
  const infoCardRef = useRef(null);
  const [infoCardHeight, setInfoCardHeight] = useState(0);
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // ID aus verschiedenen Quellen extrahieren
  const idFromParams = params.id;
  const idFromState = location.state?.requestId;
  
  // Extrahiere die ID aus der URL, wenn sie als Query-Parameter vorhanden ist
  const urlParams = new URLSearchParams(location.search);
  const queryId = urlParams.get('id');
  
  // ID für die Komponente festlegen
  let id = idFromParams || idFromState || queryId;
  
  // Fallback auf ID 4 für Testzwecke, wenn keine ID gefunden wurde
  if (!id) {
    id = '4'; // ID 4 ist die Onkologische Biomarker Studie mit Verhandlungsstatus
    console.log("Keine ID gefunden, verwende Fallback-ID 4 für Testzwecke");
  }
  
  console.log("Verwendete ID:", id, "Typ:", typeof id);
  
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counterOffer, setCounterOffer] = useState(0);
  const [editingOffer, setEditingOffer] = useState(false);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [offerAccepted, setOfferAccepted] = useState(false);
  const [requestIdError, setRequestIdError] = useState(null);
  const [negotiationData, setNegotiationData] = useState(null);
  const [negotiationLoading, setNegotiationLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  
  // Effekt zum Messen der Höhe der Anfrageinformationen-Karte
  useEffect(() => {
    const updateHeight = () => {
      if (infoCardRef.current) {
        const height = infoCardRef.current.clientHeight;
        setInfoCardHeight(height);
      }
    };

    // Initial messen
    updateHeight();

    // Event-Listener für Größenänderungen
    window.addEventListener('resize', updateHeight);

    // Erneut messen, wenn sich der Request oder die Verhandlungsdaten ändern
    const timer = setTimeout(updateHeight, 500);

    return () => {
      window.removeEventListener('resize', updateHeight);
      clearTimeout(timer);
    };
  }, [request, negotiationData, counterOffer, editingOffer, offerAccepted]);

  // Laden der Anfragedaten
  useEffect(() => {
    const loadRequest = async () => {
      try {
        // Bestimme die zu verwendende Request-ID
        let requestId;
        
        // Wenn eine ID aus der URL verfügbar ist, verwende diese
        if (id) {
          requestId = parseInt(id, 10);
          console.log("ID aus URL verfügbar:", requestId, typeof requestId);
          
          // Zusätzliche Prüfung, ob die ID gültig ist
          if (isNaN(requestId)) {
            console.error("ID konnte nicht in eine Zahl umgewandelt werden:", id);
            setRequestIdError("invalid");
            setLoading(false);
            return;
          }
        } else {
          console.error("Keine gültige ID gefunden");
          setRequestIdError("missing");
          setLoading(false);
          return;
        }
        
        console.log("Lade Anfrage mit ID:", requestId);
        const requestData = await RequestService.getRequestById(requestId);
        console.log("Geladene Anfragedaten:", requestData);
        
        if (requestData) {
          setRequest(requestData);
          
          // Setze den Gegenangebot-Wert
          if (requestData.negotiation && requestData.negotiation.counterOffer) {
            setCounterOffer(requestData.negotiation.counterOffer);
          } else if (requestData.price) {
            // Wenn kein Gegenangebot vorhanden ist, aber ein Preis, verwende diesen
            setCounterOffer(requestData.price);
          } else {
            // Fallback-Wert
            setCounterOffer(5000);
          }
          
          setLoading(false);
        } else {
          console.error("Keine Anfrage mit ID", requestId, "gefunden");
          setLoading(false);
        }
      } catch (error) {
        console.error("Fehler beim Laden der Anfrage:", error);
        setLoading(false);
      }
    };
    
    loadRequest();
    
    // Laden der Verhandlungsdaten
    const loadNegotiationData = async () => {
      if (!id) return;
      
      try {
        const parsedId = parseInt(id, 10);
        if (isNaN(parsedId)) return;
        
        console.log(`Lade Verhandlungsdaten für Anfrage mit ID ${parsedId}`);
        const response = await fetch(`/api/requests/${parsedId}/negotiation`);
        
        if (!response.ok) {
          throw new Error(`HTTP-Fehler: ${response.status}`);
        }
        
        const data = await response.json();
        console.log("Geladene Verhandlungsdaten:", data);
        setNegotiationData(data);
        
        // Setze den Gegenangebot-Wert
        if (data.counterOffer) {
          setCounterOffer(data.counterOffer);
        }
        
        setNegotiationLoading(false);
      } catch (error) {
        console.error("Fehler beim Laden der Verhandlungsdaten:", error);
        setNegotiationLoading(false);
      }
    };
    
    loadNegotiationData();
  }, [id]);

  const handleSendMessage = async (newMsg) => {
    try {
      console.log('Sending message:', newMsg);
      
      // Füge die Nachricht lokal hinzu
      const updatedMessages = [...messages, newMsg];
      
      // Aktualisiere die Verhandlungsdaten
      const updatedNegotiationData = {
        ...negotiationData,
        messages: updatedMessages
      };
      
      setNegotiationData(updatedNegotiationData);
      
      // In einer realen Anwendung würde hier die Nachricht an die API gesendet
      // await fetch(`/api/requests/${request.id}/negotiation/messages`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify(newMsg),
      // });
      
      // Simuliere eine Antwort nach einer kurzen Verzögerung
      if (newMsg.channel === 'argus_support') {
        setTimeout(() => {
          const argusResponse = {
            id: Date.now(),
            sender: 'ARGUS Support',
            senderName: 'ARGUS Support',
            senderType: 'argus',
            timestamp: new Date().toISOString(),
            content: `Danke für Ihre Anfrage. Wie kann ich Ihnen bei der Verhandlung mit ${request.company} helfen?`,
            attachments: [],
            channel: 'argus_support'
          };
          
          setMessages(prev => [...prev, argusResponse]);
          
          // Aktualisiere auch die Verhandlungsdaten
          if (negotiationData) {
            setNegotiationData(prevData => ({
              ...prevData,
              messages: [...(prevData.messages || []), argusResponse]
            }));
          }
        }, 1500);
      } else if (newMsg.channel === 'pharma') {
        setTimeout(() => {
          const pharmaResponse = {
            id: Date.now(),
            sender: request.company,
            senderName: request.company,
            senderType: 'company',
            timestamp: new Date().toISOString(),
            content: `Vielen Dank für Ihre Nachricht. Wir prüfen Ihren Vorschlag und melden uns in Kürze zurück.`,
            attachments: [],
            channel: 'pharma'
          };
          
          setMessages(prev => [...prev, pharmaResponse]);
          
          // Aktualisiere auch die Verhandlungsdaten
          if (negotiationData) {
            setNegotiationData(prevData => ({
              ...prevData,
              messages: [...(prevData.messages || []), pharmaResponse]
            }));
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Fehler beim Senden der Nachricht:', error);
    }
  };
  
  // Funktion zum Senden der Verfügbarkeitsinformationen
  const handleSendAvailabilityInfo = () => {
    if (!availabilitySummary) return;
    
    // Erstelle eine detaillierte Nachricht über die Verfügbarkeit
    let content = `Sehr geehrte Damen und Herren,\n\n`;
    content += `nach unserer Machbarkeitsprüfung möchte ich Ihnen detaillierte Informationen zur Verfügbarkeit der angeforderten Daten mitteilen:\n\n`;
    
    // Patienteninformationen
    content += `- Patientenzahl: ${availabilitySummary.patientCount} Patienten stehen für die Analyse zur Verfügung.\n\n`;
    
    // Variableninformationen
    content += `- Variablen: Von ${availabilitySummary.variablesRequested} angeforderten Variablen sind ${availabilitySummary.variablesAvailable} in unserer Datenbank verfügbar (${Math.round((availabilitySummary.variablesAvailable / availabilitySummary.variablesRequested) * 100)}%).\n\n`;
    
    // Nicht verfügbare Variablen
    if (availabilitySummary.variablesUnavailable && availabilitySummary.variablesUnavailable.length > 0) {
      content += `- Folgende Variablen sind leider nicht verfügbar:\n`;
      availabilitySummary.variablesUnavailable.forEach(variable => {
        content += `  * ${variable}\n`;
      });
      content += `\n`;
    }
    
    // Preisvorschlag
    content += `Basierend auf diesen Informationen und dem damit verbundenen Aufwand für die Datenaufbereitung und Analyse schlagen wir einen Preis von ${counterOffer} € vor.\n\n`;
    
    content += `Für Rückfragen stehe ich Ihnen gerne zur Verfügung.\n\n`;
    content += `Mit freundlichen Grüßen,\nIhr Universitätsklinikum-Team`;
    
    // Erstelle die Nachricht
    const newMessage = {
      id: Date.now(),
      sender: 'Universitätsklinikum',
      senderName: 'Universitätsklinikum',
      senderType: 'hospital',
      timestamp: new Date().toISOString(),
      content: content,
      attachments: ['Machbarkeitsbericht.pdf'],
      channel: 'pharma'
    };
    
    // Sende die Nachricht
    handleSendMessage(newMessage);
  };

  const handleAcceptOffer = async () => {
    setAcceptDialogOpen(false);
    
    try {
      // Erstelle eine neue Nachricht für die Annahme
      const acceptMessage = {
        id: messages.length > 0 ? Math.max(...messages.map(m => m.id)) + 1 : 1,
        sender: 'Universitätsklinikum',
        senderType: 'hospital',
        timestamp: new Date().toISOString(),
        content: `Wir akzeptieren Ihr Angebot von ${currentOffer} €. Wir werden den Auftrag umgehend zur Ausführung freigeben.`,
        attachments: []
      };
      
      // Füge die Nachricht lokal hinzu
      const updatedMessages = [...messages, acceptMessage];
      
      // Aktualisiere die Verhandlungsdaten
      const updatedNegotiationData = {
        ...negotiationData,
        messages: updatedMessages,
        status: 'accepted'
      };
      
      setNegotiationData(updatedNegotiationData);
      
      // In einer realen Anwendung würde hier die Annahme an die API gesendet
      // await fetch(`/api/requests/${request.id}/negotiation/accept`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({ 
      //     offerId: currentOffer,
      //     message: acceptMessage.content
      //   }),
      // });
      
      setOfferAccepted(true);
    } catch (error) {
      console.error('Fehler beim Akzeptieren des Angebots:', error);
    }
  };

  const handleRejectOffer = async () => {
    setRejectDialogOpen(false);
    
    try {
      // Erstelle eine neue Nachricht für die Ablehnung
      const rejectMessage = {
        id: messages.length > 0 ? Math.max(...messages.map(m => m.id)) + 1 : 1,
        sender: 'Universitätsklinikum',
        senderType: 'hospital',
        timestamp: new Date().toISOString(),
        content: `Wir können Ihr Angebot von ${currentOffer} € leider nicht akzeptieren. Unser Gegenangebot beträgt ${counterOffer} €.`,
        attachments: []
      };
      
      // Füge die Nachricht lokal hinzu
      const updatedMessages = [...messages, rejectMessage];
      
      // Aktualisiere die Verhandlungsdaten
      const updatedNegotiationData = {
        ...negotiationData,
        messages: updatedMessages,
        counterOffer: counterOffer
      };
      
      setNegotiationData(updatedNegotiationData);
      
      // In einer realen Anwendung würde hier die Ablehnung an die API gesendet
      // await fetch(`/api/requests/${request.id}/negotiation/reject`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({ 
      //     offerId: currentOffer,
      //     counterOffer: counterOffer,
      //     message: rejectMessage.content
      //   }),
      // });
    } catch (error) {
      console.error('Fehler beim Ablehnen des Angebots:', error);
    }
  };
  
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString('de-DE', options);
  };
  
  // Setze die Nachrichtendaten, wenn sie sich ändern
  useEffect(() => {
    const initialMessages = negotiationData && negotiationData.messages 
      ? negotiationData.messages 
      : request && request.negotiation && request.negotiation.messages 
        ? request.negotiation.messages 
        : request ? [
            {
              id: 1,
              sender: request.company,
              senderType: 'company',
              timestamp: new Date().toISOString(), // Aktuelles Datum verwenden
              content: `Wir sind an einer Analyse zu ${request.title} interessiert. Unser initiales Angebot beträgt ${request.price || 3500} €.`,
              attachments: []
            },
            {
              id: 2,
              sender: 'Universitätsklinikum',
              senderType: 'hospital',
              timestamp: new Date(new Date().getTime() - 86400000).toISOString(), // Ein Tag früher
              content: generateAvailabilityMessage(request),
              attachments: ['Machbarkeitsbericht.pdf']
            }
          ] : [];
    
    if (initialMessages.length > 0) {
      setMessages(initialMessages);
      console.log("Nachrichten gesetzt:", initialMessages);
    }
  }, [negotiationData, request, counterOffer]);
  
  // Hilfsfunktion zum Generieren einer Nachricht basierend auf der Verfügbarkeit
  const generateAvailabilityMessage = (req) => {
    if (!req) return '';
    
    // Erstelle eine Zusammenfassung der Verfügbarkeit
    const variablesRequested = req.requestedVariables ? req.requestedVariables.length : 0;
    let variablesAvailable = 0;
    const variablesUnavailable = [];
    
    if (req.requestedVariables && req.variableAvailability) {
      req.requestedVariables.forEach(variable => {
        if (req.variableAvailability[variable]) {
          variablesAvailable++;
        } else {
          variablesUnavailable.push(variable);
        }
      });
    }
    
    const allVariablesAvailable = variablesUnavailable.length === 0;
    
    // Erstelle die Nachricht
    let content = `Vielen Dank für Ihre Anfrage. Nach unserer Machbarkeitsprüfung haben wir ${req.patientCount || 'eine ausreichende Anzahl'} Patienten identifiziert, die Ihren Kriterien entsprechen.`;
    
    // Füge Informationen über die Variablenverfügbarkeit hinzu
    if (!allVariablesAvailable) {
      content += ` Allerdings sind nicht alle angeforderten Variablen in unserer Datenbank verfügbar. Von ${variablesRequested} angeforderten Variablen sind ${variablesAvailable} verfügbar.`;
      if (variablesUnavailable.length > 0 && variablesUnavailable.length <= 3) {
        content += ` Folgende Variablen sind nicht verfügbar: ${variablesUnavailable.join(', ')}.`;
      } else if (variablesUnavailable.length > 3) {
        content += ` Unter anderem sind folgende Variablen nicht verfügbar: ${variablesUnavailable.slice(0, 3).join(', ')} und ${variablesUnavailable.length - 3} weitere.`;
      }
    } else {
      content += ` Alle angeforderten Variablen sind in unserer Datenbank verfügbar.`;
    }
    
    content += ` Aufgrund des Aufwands für Datenaufbereitung und Analyse schlagen wir einen Preis von ${counterOffer} € vor.`;
    
    return content;
  };

  // Erstelle die Verfügbarkeitszusammenfassung
  const createAvailabilitySummary = () => {
    if (!request) return null;
    
    const variablesRequested = request.requestedVariables ? request.requestedVariables.length : 0;
    let variablesAvailable = 0;
    const variablesUnavailable = [];
    
    if (request.requestedVariables && request.variableAvailability) {
      request.requestedVariables.forEach(variable => {
        if (request.variableAvailability[variable]) {
          variablesAvailable++;
        } else {
          variablesUnavailable.push(variable);
        }
      });
    }
    
    return {
      patientCount: request.patientCount || 0,
      patientCountSufficient: true, // Annahme: Wenn patientCount vorhanden ist, ist es ausreichend
      variablesRequested,
      variablesAvailable,
      variablesUnavailable,
      allVariablesAvailable: variablesUnavailable.length === 0
    };
  };

  // Bestimme die Angebotsdaten
  const initialOffer = negotiationData && negotiationData.initialOffer 
    ? negotiationData.initialOffer 
    : request && request.negotiation && request.negotiation.initialOffer 
      ? request.negotiation.initialOffer 
      : request && request.price ? request.price : 3500;
  
  const currentOffer = negotiationData && negotiationData.currentOffer 
    ? negotiationData.currentOffer 
    : request && request.negotiation && request.negotiation.currentOffer 
      ? request.negotiation.currentOffer 
      : initialOffer;
      
  // Verfügbarkeitszusammenfassung
  const availabilitySummary = negotiationData && negotiationData.availabilitySummary 
    ? negotiationData.availabilitySummary 
    : createAvailabilitySummary();

  // Rendere verschiedene Zustände basierend auf loading und Fehler
  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (requestIdError === "invalid") {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Ungültige Anfrage-ID.
          </Alert>
          <Button variant="contained" onClick={() => navigate('/incoming-requests')}>
            Zurück zur Übersicht
          </Button>
        </Box>
      </MainLayout>
    );
  }

  if (requestIdError === "missing") {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Keine Anfrage-ID gefunden.
          </Alert>
          <Button variant="contained" onClick={() => navigate('/incoming-requests')}>
            Zurück zur Übersicht
          </Button>
        </Box>
      </MainLayout>
    );
  }

  if (!request) {
    return (
      <MainLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Die angeforderte Anfrage konnte nicht gefunden werden.
          </Alert>
          <Button variant="contained" onClick={() => navigate('/incoming-requests')}>
            Zurück zur Übersicht
          </Button>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Paper elevation={2} sx={{ p: 0, pt: 0, mb: 4, borderRadius: 2, overflow: 'hidden' }}>
          <ProcessStepper 
            activeStep={2} 
            title="Bearbeitungsstatus der Anfrage" 
            requestStatus={request.status} 
            requestId={request.id}
          />
          <Box sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
            <Typography variant="overline" display="block" sx={{ color: 'text.secondary', letterSpacing: 1.5, mb: 0.5 }}>
              VERHANDLUNG
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {request.title}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
              Anfrage von {request.company} • Eingangsdatum: {DateUtils.formatDate(request.date)}
              {request.statusHistory && request.statusHistory.negotiation && (
                <Chip 
                  size="small" 
                  label={`Bearbeitungszeitraum: ${DateUtils.formatProcessingPeriod(request.statusHistory.negotiation.startDate, request.statusHistory.negotiation.endDate)}`} 
                  color="primary" 
                  variant="outlined"
                  sx={{ ml: 2, height: 24, fontSize: '0.75rem' }}
                />
              )}
            </Typography>
          </Box>
        </Paper>

        {offerAccepted && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Das Angebot wurde angenommen. Der Auftrag kann nun zur Ausführung weitergeleitet werden.
          </Alert>
        )}

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/incoming-requests')}
            startIcon={<ChevronLeftIcon />}
          >
            Zurück zur Übersicht
          </Button>
          
          <Box>
            <Chip 
              label={`Status: Verhandlung`} 
              color="primary" 
              variant="outlined" 
              sx={{ fontWeight: 'medium', mr: 1 }}
            />
            <Chip 
              label={`Patienten: ${request.patientCount || 'Wird ermittelt'}`} 
              color="secondary" 
              variant="outlined" 
              sx={{ fontWeight: 'medium' }}
            />
          </Box>
        </Box>
        
        <Grid container spacing={3}>
          {availabilitySummary && (
            <Grid item xs={12}>
              <AvailabilitySummary availabilitySummary={availabilitySummary} />
            </Grid>
          )}
          
          <Grid item xs={12} md={4}>
            <Card ref={infoCardRef} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <CardHeader 
                title="Anfrageinformationen" 
                sx={{ 
                  bgcolor: 'rgba(255, 122, 0, 0.05)', 
                  borderBottom: '1px solid', 
                  borderColor: 'rgba(255, 122, 0, 0.1)' 
                }}
              />
              <Divider />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {request.title}
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Unternehmen:</strong> {request.company}
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Eingangsdatum:</strong> {DateUtils.formatDate(request.date)}
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>Patientenzahl:</strong> {request.patientCount || 'Noch nicht ermittelt'}
                </Typography>
                
                {availabilitySummary && (
                  <AvailabilitySummary availabilitySummary={availabilitySummary} compact={true} />
                )}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ 
                  p: 2, 
                  mb: 2, 
                  bgcolor: 'background.default', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Preisverhandlung
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Initiales Angebot:</Typography>
                    <Typography variant="body1" fontWeight="medium">{initialOffer.toLocaleString('de-DE')} €</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Aktuelles Angebot:</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary.main">{currentOffer.toLocaleString('de-DE')} €</Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  p: 2, 
                  mb: 2, 
                  bgcolor: 'primary.light', 
                  color: 'white',
                  borderRadius: 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                }}>
                  <Typography variant="subtitle2" sx={{ color: 'white', opacity: 0.9 }} gutterBottom>
                    Ihr Gegenangebot
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {editingOffer ? (
                      <TextField
                        size="small"
                        type="number"
                        value={counterOffer}
                        onChange={(e) => setCounterOffer(e.target.value)}
                        InputProps={{
                          endAdornment: <Typography sx={{ color: 'white' }}>€</Typography>,
                          sx: { 
                            bgcolor: 'rgba(255,255,255,0.15)', 
                            color: 'white',
                            '& .MuiInputBase-input': { color: 'white' }
                          }
                        }}
                        sx={{ width: '150px' }}
                      />
                    ) : (
                      <Typography variant="h6" fontWeight="bold">
                        {parseInt(counterOffer).toLocaleString('de-DE')} €
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => setEditingOffer(!editingOffer)}
                      startIcon={<EditIcon fontSize="small" />}
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)', 
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } 
                      }}
                    >
                      {editingOffer ? 'Speichern' : 'Bearbeiten'}
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Aktionen
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      color="success"
                      size="large"
                      fullWidth
                      startIcon={<CheckCircleIcon />}
                      onClick={() => setAcceptDialogOpen(true)}
                      disabled={offerAccepted}
                      sx={{ py: 1.5 }}
                    >
                      Angebot annehmen
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="error"
                      fullWidth
                      startIcon={<CancelIcon />}
                      onClick={() => setRejectDialogOpen(true)}
                      disabled={offerAccepted}
                    >
                      Angebot ablehnen
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="info"
                      fullWidth
                      startIcon={<InfoIcon />}
                      onClick={handleSendAvailabilityInfo}
                      disabled={offerAccepted}
                    >
                      Verfügbarkeitsinformationen senden
                    </Button>
                  </Box>
                </Box>
                {offerAccepted && (
                  <Button 
                    variant="contained" 
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => navigate(`/order-execution?id=${request.id}`)}
                  >
                    Zur Auftragsausführung
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ 
              p: 0, 
              borderRadius: 2, 
              height: infoCardHeight > 0 ? `${infoCardHeight}px` : '870px', 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'background.default', 
                borderBottom: '1px solid', 
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Kommunikation mit {request.company}
                </Typography>
                <Chip 
                  label={messages.length + " Nachrichten"} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              </Box>
              <ChatInterface 
                chatId={request.id}
                participants={[
                  { id: 'hospital', name: 'Universitätsklinikum', type: 'hospital' },
                  { id: request.company, name: request.company, type: 'company' },
                  { id: 'argus', name: 'ARGUS Support', type: 'argus' }
                ]}
                initialMessages={messages.map(msg => ({
                  id: msg.id,
                  sender: msg.sender,
                  senderName: msg.sender,
                  senderType: msg.senderType,
                  timestamp: msg.timestamp,
                  content: msg.content,
                  attachments: msg.attachments ? (Array.isArray(msg.attachments) ? msg.attachments.map((att, idx) => ({
                    id: idx,
                    name: att,
                    type: att.endsWith('.pdf') ? 'application/pdf' : 'text/plain',
                    size: '1.2 MB'
                  })) : [])
                  : []
                }))}
                onSendMessage={handleSendMessage}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Dialog für Angebotsannahme */}
        <Dialog
          open={acceptDialogOpen}
          onClose={() => setAcceptDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxWidth: 500
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
            <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              Angebot annehmen
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Möchten Sie das aktuelle Angebot von {currentOffer} € annehmen? 
              Nach der Annahme wird der Auftrag zur Ausführung freigegeben.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAcceptDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleAcceptOffer} variant="contained" color="success">
              Angebot annehmen
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog für Angebotsablehnung */}
        <Dialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxWidth: 500
            }
          }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
            <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CancelIcon color="error" />
              Angebot ablehnen
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Möchten Sie das aktuelle Angebot wirklich ablehnen? 
              Sie können ein neues Gegenangebot unterbreiten oder die Verhandlung beenden.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Begründung (optional)"
              fullWidth
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleRejectOffer} variant="contained" color="error">
              Angebot ablehnen
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default NegotiationPage;
